import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import Skeleton from '../ui/Skeleton';
import { AnimatedCounter, SkeletonListItem } from '../ui';
import { seoService } from '../../services/seoService';
import { useWebsite } from '../../contexts/WebsiteContext';
import { Empty, Eyebrow, HairlineGrid, PageHeader, Panel, Pill, Td, Th } from '../ui/kit';
import { MARKS } from '../../lib/marks';

const SEOPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { websites, selectedWebsiteId: ctxWebsiteId, isAllSelected } = useWebsite();

  // SEO requires a specific site — fall back to first website when "All" is selected
  const websiteId = isAllSelected ? (websites[0]?.id || '') : ctxWebsiteId;

  // Fetch keywords
  const { data: keywords = [], isLoading: keywordsLoading } = useQuery({
    queryKey: ['seo-keywords', websiteId],
    queryFn: () => seoService.getKeywords(websiteId),
    enabled: !!websiteId,
  });

  // Fetch PageSpeed
  const { data: pageSpeed, isLoading: pageSpeedLoading } = useQuery({
    queryKey: ['seo-pagespeed', websiteId],
    queryFn: () => seoService.getPageSpeed(websiteId),
    enabled: !!websiteId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  const isLoading = keywordsLoading || pageSpeedLoading;

  const handleRefreshKeywords = async () => {
    if (!websiteId) return;
    await seoService.refreshKeywords(websiteId);
    await queryClient.invalidateQueries({ queryKey: ['seo-keywords', websiteId] });
  };

  // Compute stats from real data
  const topTenCount = keywords.filter(k => k.currentPosition <= 10).length;
  const improvedCount = keywords.filter(k => k.currentPosition < k.previousPosition).length;
  const declinedCount = keywords.filter(k => k.currentPosition > k.previousPosition).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Search"
        title="Search Keywords"
        description="Monitor the search terms that help customers find your website"
        actions={
          <Pill
            onClick={handleRefreshKeywords}
            tone="outline"
            size="sm"
            title="Refresh rankings"
          >
            <RefreshIcon className="w-4 h-4" />
            Refresh
          </Pill>
        }
      />

      {!websiteId ? (
        <Empty
          mark={MARKS.seo}
          eyebrow="Search"
          title="No website to track"
          description="Keyword positions and page health show up here once your site is live."
        />
      ) : (
        <>
          {/* PageSpeed Score and Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {isLoading ? (
              <>
                <Panel className="p-6">
                  <Skeleton variant="circular" width={120} height={120} className="mx-auto mb-4" />
                  <Skeleton variant="text" width={100} height={20} className="mx-auto" />
                </Panel>
                <Panel className="p-6 md:col-span-2">
                  <Skeleton variant="text" width={120} height={20} className="mb-4" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="text-center p-3 bg-zinc-50 rounded-xl">
                        <Skeleton variant="text" width={40} height={28} className="mx-auto mb-1" />
                        <Skeleton variant="text" width={80} height={12} className="mx-auto" />
                      </div>
                    ))}
                  </div>
                </Panel>
              </>
            ) : (
              <>
                {/* PageSpeed Score Card */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0, duration: 0.3 }}
                  className="flex flex-col items-center justify-center rounded-xl border border-zinc-200 bg-white p-6 transition-colors hover:border-zinc-300"
                >
                  <Eyebrow className="mb-4">Performance Score</Eyebrow>
                  <CircularProgress score={pageSpeed?.performance ?? 0} />
                  <p className="mt-4 text-[15px] text-muted">
                    {(pageSpeed?.performance ?? 0) >= 90 ? 'Excellent' : (pageSpeed?.performance ?? 0) >= 50 ? 'Needs Improvement' : 'Poor'}
                  </p>
                </motion.div>

                {/* Quick Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                  className="rounded-xl border border-zinc-200 bg-white p-6 transition-colors hover:border-zinc-300 md:col-span-2"
                >
                  <Eyebrow className="mb-4">Quick Stats</Eyebrow>
                  <HairlineGrid cols="grid-cols-2 sm:grid-cols-4">
                    {[
                      { value: topTenCount, label: 'Top 10 Keywords' },
                      { value: improvedCount, label: 'Improved' },
                      { value: declinedCount, label: 'Declined' },
                      { value: keywords.length, label: 'Total Tracked' },
                    ].map((stat, idx) => (
                      <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 + idx * 0.05, duration: 0.2 }}
                        className="bg-white p-4 text-center"
                      >
                        <p className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-primary">{stat.value}</p>
                        <Eyebrow className="mt-2">{stat.label}</Eyebrow>
                      </motion.div>
                    ))}
                  </HairlineGrid>
                </motion.div>
              </>
            )}
          </div>

          {/* Keyword Rankings Table */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.3 }}
            className="overflow-hidden rounded-xl border border-zinc-200 bg-white"
          >
            <div className="border-b border-zinc-200 px-6 py-4">
              <Eyebrow>Keyword Rankings</Eyebrow>
              <p className="mt-2 text-[15px] text-muted">
                {keywords.length > 0
                  ? `Tracking ${keywords.length} keywords`
                  : 'No keywords tracked yet. Add keywords in SerpBear to start tracking.'}
              </p>
            </div>
            {isLoading ? (
              <div className="divide-y divide-zinc-100">
                {[...Array(5)].map((_, i) => (
                  <SkeletonListItem key={i} />
                ))}
              </div>
            ) : keywords.length === 0 ? (
              <Empty title="No keywords tracked" description="No keywords are being tracked for this website yet." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-paper-tint">
                    <tr>
                      <Th className="px-6">Keyword</Th>
                      <Th className="px-6 text-center">Position</Th>
                      <Th className="px-6 text-center">Change</Th>
                      <Th className="px-6 text-right">URL</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((keyword, index) => {
                      const change = keyword.previousPosition - keyword.currentPosition;
                      const isImproved = change > 0;

                      return (
                        <motion.tr
                          key={keyword.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05, duration: 0.2 }}
                          className="transition-colors hover:bg-paper-tint"
                        >
                          <Td className="px-6">
                            <span className="text-[15px] font-medium text-primary">{keyword.keyword}</span>
                          </Td>
                          <Td className="px-6 text-center">
                            <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-zinc-100 px-2 font-mono text-[13px] font-semibold text-zinc-700 ring-1 ring-zinc-900/5">
                              {keyword.currentPosition}
                            </span>
                          </Td>
                          <Td className="px-6 text-center">
                            {change !== 0 ? (
                              <span className={`inline-flex items-center gap-1 text-[13px] font-medium ${
                                isImproved ? 'text-emerald-700' : 'text-red-700'
                              }`}>
                                {isImproved ? (
                                  <ArrowUpIcon className="w-4 h-4" />
                                ) : (
                                  <ArrowDownIcon className="w-4 h-4" />
                                )}
                                {Math.abs(change)}
                              </span>
                            ) : (
                              <span className="text-[13px] text-muted">-</span>
                            )}
                          </Td>
                          <Td className="px-6 text-right">
                            <span className="inline-block max-w-[200px] truncate font-mono text-[13px] text-muted">
                              {keyword.url ? (() => { try { return new URL(keyword.url).pathname; } catch { return keyword.url; } })() : '-'}
                            </span>
                          </Td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* PageSpeed Details */}
          {pageSpeed && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Core Web Vitals */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className="rounded-xl border border-zinc-200 bg-white p-6 transition-colors hover:border-zinc-300"
              >
                <Eyebrow className="mb-4">Core Web Vitals</Eyebrow>
                <div className="space-y-4">
                  {[
                    { label: 'First Contentful Paint', value: `${(pageSpeed.fcp / 1000).toFixed(1)}s`, good: pageSpeed.fcp < 1800 },
                    { label: 'Largest Contentful Paint', value: `${(pageSpeed.lcp / 1000).toFixed(1)}s`, good: pageSpeed.lcp < 2500 },
                    { label: 'Cumulative Layout Shift', value: pageSpeed.cls.toFixed(3), good: pageSpeed.cls < 0.1 },
                    { label: 'Total Blocking Time', value: `${pageSpeed.tbt}ms`, good: pageSpeed.tbt < 200 },
                  ].map((metric) => (
                    <div key={metric.label} className="flex items-center justify-between rounded-xl bg-paper-tint p-3">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${metric.good ? 'bg-green-500' : 'bg-orange-500'}`} />
                        <span className="text-[15px] text-primary">{metric.label}</span>
                      </div>
                      <span className={`font-mono text-[13px] font-semibold ${metric.good ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {metric.value}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Lighthouse Scores */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="rounded-xl border border-zinc-200 bg-white p-6 transition-colors hover:border-zinc-300"
              >
                <Eyebrow className="mb-4">Lighthouse Scores</Eyebrow>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Performance', score: pageSpeed.performance },
                    { label: 'Accessibility', score: pageSpeed.accessibility },
                    { label: 'Best Practices', score: pageSpeed.bestPractices },
                    { label: 'SEO', score: pageSpeed.seo },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl bg-paper-tint p-4 text-center">
                      <div className="relative w-16 h-16 mx-auto mb-2">
                        <CircularProgressSmall score={item.score} />
                      </div>
                      <Eyebrow>{item.label}</Eyebrow>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                  Last updated: {new Date(pageSpeed.fetchedAt).toLocaleString()}
                </p>
              </motion.div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Circular Progress Component
interface CircularProgressProps {
  score: number;
}

const CircularProgress: React.FC<CircularProgressProps> = ({ score }) => {
  const circumference = 2 * Math.PI * 52;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full transform -rotate-90">
        <circle cx="64" cy="64" r="52" stroke="currentColor" strokeWidth="8" fill="none" className="text-zinc-200" />
        <motion.circle
          cx="64" cy="64" r="52"
          stroke={getScoreColor(score)}
          strokeWidth="8" fill="none" strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-primary">
          <AnimatedCounter value={score} />
        </span>
        <span className="text-xs text-muted">out of 100</span>
      </div>
    </div>
  );
};

const CircularProgressSmall: React.FC<CircularProgressProps> = ({ score }) => {
  const circumference = 2 * Math.PI * 26;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="relative w-16 h-16">
      <svg className="w-full h-full transform -rotate-90">
        <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="5" fill="none" className="text-zinc-200" />
        <motion.circle
          cx="32" cy="32" r="26"
          stroke={getScoreColor(score)}
          strokeWidth="5" fill="none" strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-primary">{score}</span>
      </div>
    </div>
  );
};

// Icons
const ArrowUpIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
  </svg>
);

const ArrowDownIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

export default SEOPage;
